// Contracts subtree state — write-through cache backed by the V2
// contracts API. Pages dispatch CREATE_BUNDLE / REPLACE_BUNDLE after
// every mutation so the detail page renders immediately without a
// second fetch.
//
// Initial state loads via `refresh()` from the server when the
// provider mounts. The viewer is the authenticated user from the
// JWT — no demo viewer toggle anymore.

import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import type {
  ContractBundle,
  ProjectType,
  AccountType,
  FundCoverageView,
} from './types';
import {
  ACCEPTANCE_PERIOD_DEFAULTS,
  ACCEPTANCE_PERIOD_COMPANY_BONUS,
} from './types';
import { contractApiService } from '../../services/contractApiService';
import { userService } from '../../services/userService';

// =====================================================================
// Selectors
// =====================================================================

export function selectFundCoverage(bundle: ContractBundle): FundCoverageView {
  const { contract, releasePlan } = bundle;
  const availableBalance = contract.totalPaid - contract.totalReleased - contract.totalRefunded;

  let cumulative = 0;
  const perMilestone = releasePlan
    .slice()
    .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
    .map(rp => {
      cumulative += rp.amount;
      return {
        milestoneId: rp.milestoneId,
        cumulativeReleaseRequired: cumulative,
        // Covered if: paid-so-far minus released-so-far is enough to fund this entry.
        // (Tail is excluded from totalPaid until tail-pay actually posts.)
        isCovered: contract.totalPaid - contract.totalReleased >= cumulative,
      };
    });

  return {
    totalPaid: contract.totalPaid,
    totalReleased: contract.totalReleased,
    totalRefunded: contract.totalRefunded,
    availableBalance,
    tailAmount: contract.tailAmount,
    perMilestone,
  };
}

export function isMilestoneAssetCovered(bundle: ContractBundle, milestoneId: string): boolean {
  const cov = selectFundCoverage(bundle);
  return cov.perMilestone.find(m => m.milestoneId === milestoneId)?.isCovered ?? false;
}

export function computeAcceptancePeriodDays(projectType: ProjectType, buyerAccountType: AccountType): number {
  const base = ACCEPTANCE_PERIOD_DEFAULTS[projectType];
  return base + (buyerAccountType === 'company' ? ACCEPTANCE_PERIOD_COMPANY_BONUS : 0);
}

// =====================================================================
// Reducer
// =====================================================================

export type Action =
  | { type: 'SET_BUNDLES'; bundles: ContractBundle[] }
  | { type: 'CREATE_BUNDLE'; bundle: ContractBundle }
  | { type: 'REPLACE_BUNDLE'; bundle: ContractBundle }
  | { type: 'SET_LOADING'; loading: boolean };

interface State {
  bundles: Record<string, ContractBundle>;
  loading: boolean;
}

function initialState(): State {
  return { bundles: {}, loading: true };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_BUNDLES': {
      const next: Record<string, ContractBundle> = {};
      for (const b of action.bundles) next[b.contract.id] = b;
      return { ...state, bundles: next, loading: false };
    }
    case 'CREATE_BUNDLE':
    case 'REPLACE_BUNDLE':
      return { ...state, bundles: { ...state.bundles, [action.bundle.contract.id]: action.bundle } };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    default:
      return state;
  }
}

// =====================================================================
// Context
// =====================================================================

interface ContractsContextValue {
  bundles: ContractBundle[];
  bundleById(id: string): ContractBundle | undefined;
  viewerId: string;
  // Whether the viewer has completed real-name verification — used to
  // pre-check before kicking off e签宝 signing.
  viewerVerified: boolean;
  isViewerClient(bundle: ContractBundle): boolean;
  isViewerCreator(bundle: ContractBundle): boolean;
  loading: boolean;
  refresh(): Promise<void>;
  loadDetail(contractId: string): Promise<void>;
  dispatch: React.Dispatch<Action>;
}

const Ctx = createContext<ContractsContextValue | null>(null);

export function ContractsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  // viewerId is the authenticated user's real id, resolved from the
  // profile API (auth only stores the JWT, not the profile). Empty
  // until the profile loads.
  const [viewerId, setViewerId] = useState('');
  const [viewerVerified, setViewerVerified] = useState(false);

  const refresh = async () => {
    dispatch({ type: 'SET_LOADING', loading: true });
    const result = await contractApiService.listForUser();
    if (result.success) {
      dispatch({ type: 'SET_BUNDLES', bundles: result.bundles });
    } else {
      dispatch({ type: 'SET_BUNDLES', bundles: [] });
    }
  };

  const loadDetail = async (contractId: string) => {
    const result = await contractApiService.getBundle(contractId);
    if (result.success) {
      dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    }
  };

  useEffect(() => {
    // Resolve the current user id from the profile API.
    (async () => {
      try {
        const res = await userService.getUserProfile();
        const prof = (res as any)?.data?.userProfile;
        if (prof?.userId) setViewerId(String(prof.userId));
        setViewerVerified(Boolean(prof?.verified));
      } catch {
        // leave empty; pages handle the not-logged-in case
      }
    })();
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<ContractsContextValue>(() => {
    const bundlesArr = Object.values(state.bundles);
    return {
      bundles: bundlesArr,
      bundleById: (id) => state.bundles[id],
      viewerId,
      viewerVerified,
      isViewerClient: (b) => b.contract.client.userId === viewerId,
      isViewerCreator: (b) => b.contract.creator.userId === viewerId,
      loading: state.loading,
      refresh,
      loadDetail,
      dispatch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, viewerId, viewerVerified]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useContracts(): ContractsContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useContracts must be inside ContractsProvider');
  return v;
}
